<template>
  <b-container>
    <b-modal v-model="pivotEditVisible" title="edit a pivot table" @ok="save" ok-title="OK" size="xl" modal-class="view-editor-modal">
      <template #title>
        edit a pivot table
        <modal-help topic="edit-pivot" />
      </template>
      <div class="edit-modal-content">
        <b-tabs v-model="activeTab" content-class="tab-body" nav-class="view-editor-tabs" pills>
          <b-tab title="General">
            <div class="tab-section">
              <div class="section-heading">Naming</div>
              <div class="field-row">
                <label class="field-label">
                  unique title
                  <b-form-input v-model="uniqueTitle" placeholder="pivot unique title" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title
                  <b-form-input v-model="title" placeholder="pivot title" required />
                </label>
              </div>
            </div>
            <div class="tab-section">
              <div class="section-heading">Data Source</div>
              <div class="data-source-display">
                <span class="data-source-spreadsheet">{{ dataSourceLabel }}</span>
                <span v-if="filterByPageRecord" class="data-source-badge">filtered by page record</span>
              </div>
              <label v-if="pageRecordLink" class="checkbox-label page-record-filter">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p v-if="pageRecordLink" class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. the view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
            </div>
            <div class="tab-section">
              <div class="section-heading">Realtime</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="realtime" />
                enable realtime updates (the pivot will recalculate when records change)
              </label>
            </div>
          </b-tab>

          <b-tab title="Pivot">
            <p class="section-desc">Rows group the left side. Columns group the top. Values are the numbers in each intersection, the same way a pivot table works in Excel. Choosing a field adds it.</p>
            <div v-if="selectionProblemsList.length" class="pivot-save-error" role="alert">
              <p>These fields cannot be saved. Remove them, or choose a column from this spreadsheet, before OK.</p>
              <ul>
                <li v-for="problem in selectionProblemsList" :key="problem.area + problem.field">{{ problem.area }}: {{ problem.message }}</li>
              </ul>
            </div>
            <div class="pivot-wells">
              <section class="pivot-well">
                <h3>Rows</h3>
                <ul v-if="rowFields.length" class="pivot-field-list">
                  <li v-for="(field, index) in rowFields" :key="field">
                    <span>{{ fieldLabel(field) }}</span>
                    <p v-if="fieldProblem(field)" class="pivot-field-error">{{ fieldProblem(field) }}</p>
                    <span class="pivot-field-actions">
                      <button type="button" @click="moveField(rowFields, index, 'up')" :disabled="index === 0">up</button>
                      <button type="button" @click="moveField(rowFields, index, 'down')" :disabled="index === rowFields.length - 1">down</button>
                      <button type="button" @click="removeField(rowFields, index)">remove</button>
                    </span>
                  </li>
                </ul>
                <p v-else class="section-desc">No row fields yet. Choose a column below to group records down the left.</p>
                <div class="pivot-add">
                  <b-form-select :model-value="rowChoice" :options="addOptions(rowFields)" size="sm" @update:model-value="chooseRow" />
                </div>
              </section>

              <section class="pivot-well">
                <h3>Columns</h3>
                <ul v-if="columnFields.length" class="pivot-field-list">
                  <li v-for="(field, index) in columnFields" :key="field">
                    <span>{{ fieldLabel(field) }}</span>
                    <p v-if="fieldProblem(field)" class="pivot-field-error">{{ fieldProblem(field) }}</p>
                    <span class="pivot-field-actions">
                      <button type="button" @click="moveField(columnFields, index, 'up')" :disabled="index === 0">up</button>
                      <button type="button" @click="moveField(columnFields, index, 'down')" :disabled="index === columnFields.length - 1">down</button>
                      <button type="button" @click="removeField(columnFields, index)">remove</button>
                    </span>
                  </li>
                </ul>
                <p v-else class="section-desc">No column fields yet. Choose a column below to spread values across the top.</p>
                <div class="pivot-add">
                  <b-form-select :model-value="columnChoice" :options="addOptions(columnFields)" size="sm" @update:model-value="chooseColumn" />
                </div>
              </section>

              <section class="pivot-well">
                <h3>Values</h3>
                <ul v-if="valueFields.length" class="pivot-field-list">
                  <li v-for="(value, index) in valueFields" :key="'value-' + index">
                    <span>{{ valueHeading(value) }}</span>
                    <p v-if="fieldProblem(value.field)" class="pivot-field-error">{{ fieldProblem(value.field) }}</p>
                    <label class="pivot-value-label">
                      heading
                      <b-form-input v-model="value.label" size="sm" :placeholder="valueHeading(value, true)" />
                    </label>
                    <b-form-select v-model="value.aggregation" :options="aggregationOptions" size="sm" />
                    <span class="pivot-field-actions">
                      <button type="button" @click="moveField(valueFields, index, 'up')" :disabled="index === 0">up</button>
                      <button type="button" @click="moveField(valueFields, index, 'down')" :disabled="index === valueFields.length - 1">down</button>
                      <button type="button" @click="removeField(valueFields, index)">remove</button>
                    </span>
                  </li>
                </ul>
                <p v-else class="section-desc">No value fields yet. The pivot counts records until you add one.</p>
                <div class="pivot-add">
                  <b-form-select :model-value="valueChoice" :options="valueAddOptions" size="sm" @update:model-value="chooseValue" />
                </div>
              </section>
            </div>
            <div class="tab-section pivot-totals">
              <div class="section-heading">Totals</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="showSubtotals" />
                show subtotals when more than one row field is set
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="showRowGrandTotal" />
                show a grand total row
              </label>
              <label class="checkbox-label">
                <input type="checkbox" v-model="showColumnGrandTotal" />
                show a grand total column
              </label>
            </div>
          </b-tab>

          <b-tab title="Filtering">
            <div v-if="pageRecordLink" class="tab-section">
              <div class="section-heading">Page record</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
            </div>
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="searchEnabled" />
                show a search box. visitors can type text to narrow the records in this pivot.
              </label>
              <div v-if="searchEnabled" class="search-scope">
                <label class="checkbox-label">
                  <input type="radio" value="view" v-model="searchScope" />
                  search the row, column, and value fields
                </label>
                <label class="checkbox-label">
                  <input type="radio" value="spreadsheet" v-model="searchScope" />
                  search every field on the spreadsheet
                </label>
                <p v-if="searchScope === 'spreadsheet'" class="section-desc search-security-note">
                  Searching every field on the spreadsheet matches columns that are not shown on this view. A visitor can learn that a hidden value exists even though that column stays off the view. Password fields are never searched.
                </p>
              </div>
            </div>
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="userFiltering" />
                allow users to filter records
              </label>
              <label v-if="userFiltering" class="checkbox-label" style="margin-top: 8px;">
                <input type="checkbox" v-model="restrictUserFiltering" />
                restrict filter fields to the fields used by this pivot
              </label>
            </div>
            <div v-if="userFiltering" class="tab-section">
              <div class="section-heading">Default Filters</div>
              <p class="section-desc">these filters will be displayed by default on the component, and users can change them</p>
              <PreFilterBuilder
                :filters="defaultUserFilters"
                :match-mode="defaultUserFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="defaultUserFilters = $event"
                @update:match-mode="defaultUserFilterMatchMode = $event"
              />
            </div>
            <div class="tab-section">
              <div class="section-heading">Prefilters</div>
              <p class="section-desc">these filters will always be applied to the record set and will not be displayed on the view</p>
              <PreFilterBuilder
                :filters="preFilters"
                :match-mode="preFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="preFilters = $event"
                @update:match-mode="preFilterMatchMode = $event"
              />
            </div>
            <FilterButtonEditor
              :buttons="filterButtons"
              :allow-multiple="allowMultipleFilterButtons"
              :spreadsheet="spreadsheet"
              :all-spreadsheets="allSpreadsheets"
              :application-unique-title="applicationUniqueTitle"
              @update:buttons="filterButtons = $event"
              @update:allow-multiple="allowMultipleFilterButtons = $event"
            />
          </b-tab>
        </b-tabs>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
  import PreFilterBuilder from './lib/PreFilterBuilder.vue'
  import FilterButtonEditor from './lib/FilterButtonEditor.vue'
  import { moveListItem } from '../../../shared/list-move.js'
  import { AGGREGATIONS, AGGREGATION_LABELS, normalizePivotConfig, pivotFieldProblem, pivotSelectionProblems } from '../../../shared/pivot-table.js'
  import { relatedViewColumnOptions, relatedSpecFromKey } from '../../../shared/related-view-columns.js'
  import { pageRecordRelationship } from '../lib/page-record-link.js'

  const SKIPPED_TYPES = new Set(['password', 'image', 'file', 'relationship', 'richText'])

  export default {
    name: 'pivotEdit',
    components: { PreFilterBuilder, FilterButtonEditor },
    data () {
      return {
        pivotEditVisible: false,
        activeTab: 0,
        uniqueTitle: '',
        title: '',
        spreadSheetUniqueTitle: '',
        spreadsheet: null,
        allSpreadsheets: [],
        fieldOptions: [],
        rowFields: [],
        columnFields: [],
        valueFields: [],
        rowChoice: '',
        columnChoice: '',
        valueChoice: '',
        selectionProblemsList: [],
        showSubtotals: true,
        showRowGrandTotal: true,
        showColumnGrandTotal: true,
        realtime: false,
        userFiltering: false,
        searchEnabled: false,
        searchScope: 'view',
        restrictUserFiltering: false,
        defaultUserFilters: [],
        defaultUserFilterMatchMode: 'all',
        preFilters: [],
        preFilterMatchMode: 'all',
        filterButtons: [],
        allowMultipleFilterButtons: false,
        parentDesigner: null,
        filterByPageRecord: false,
        pageRecordSpreadSheetUniqueTitle: '',
        relationshipColumnTitle: '',
        applicationUniqueTitle: '',
        aggregationOptions: AGGREGATIONS.map(value => ({ value, text: AGGREGATION_LABELS[value] }))
      }
    },
    computed: {
      pageRecordLink () {
        return pageRecordRelationship(this.allSpreadsheets, this.spreadSheetUniqueTitle, this.pageRecordSpreadSheetUniqueTitle)
      },
      dataSourceLabel () {
        const title = (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'unknown'
        if (!this.filterByPageRecord) return title
        const link = this.pageRecordLink
        const pageRecordTitle = link ? link.pageRecordTitle : this.pageRecordSpreadSheetUniqueTitle
        const columnTitle = link ? link.columnTitle : this.relationshipColumnTitle
        let label = title
        if (pageRecordTitle) label += ` — related to page's ${pageRecordTitle} record`
        if (columnTitle) label += ` via ${columnTitle}`
        return label
      },
      valueAddOptions () {
        return [{ value: '', text: 'select a field' }, ...this.fieldOptions]
      }
    },
    methods: {
      fieldLabel (field) {
        const option = this.fieldOptions.find(item => item.value === field)
        return option ? option.text : field
      },
      fieldType (field) {
        const option = this.fieldOptions.find(item => item.value === field)
        return option ? option.type : ''
      },
      valueHeading (value, ignoreCustom) {
        if (!ignoreCustom && value.label && value.label.trim()) return value.label.trim()
        const name = this.fieldLabel(value.field)
        return `${AGGREGATION_LABELS[value.aggregation] || 'Sum'} of ${name}`
      },
      addOptions (selected) {
        const used = new Set(selected)
        return [
          { value: '', text: 'select a field' },
          ...this.fieldOptions.filter(option => !used.has(option.value))
        ]
      },
      spreadsheetColumns () {
        return (this.spreadsheet && this.spreadsheet.columns) || null
      },
      relatedFieldKeys () {
        return this.fieldOptions
          .map(option => option.value)
          .filter(value => relatedSpecFromKey(value))
      },
      fieldProblem (field) {
        if (!this.spreadsheet) return ''
        return pivotFieldProblem(field, this.spreadsheet.columns || [], this.relatedFieldKeys())
      },
      currentProblems () {
        return pivotSelectionProblems({
          rowFields: this.rowFields,
          columnFields: this.columnFields,
          valueFields: this.valueFields
        }, this.spreadsheetColumns(), this.relatedFieldKeys())
      },
      refreshProblems () {
        this.selectionProblemsList = this.currentProblems()
      },
      addField (list, choice) {
        if (!choice || list.includes(choice)) return false
        const message = this.spreadsheet ? pivotFieldProblem(choice, this.spreadsheet.columns || [], this.relatedFieldKeys()) : ''
        if (message) {
          this.selectionProblemsList = [{ area: 'Pivot', field: choice, message }]
          return false
        }
        list.push(choice)
        this.selectionProblemsList = this.currentProblems()
        return true
      },
      chooseRow (value) {
        this.rowChoice = ''
        if (!value) return
        this.addField(this.rowFields, value)
      },
      chooseColumn (value) {
        this.columnChoice = ''
        if (!value) return
        this.addField(this.columnFields, value)
      },
      chooseValue (value) {
        this.valueChoice = ''
        if (!value) return
        const message = this.spreadsheet ? pivotFieldProblem(value, this.spreadsheet.columns || [], this.relatedFieldKeys()) : ''
        if (message) {
          this.selectionProblemsList = [{ area: 'Values', field: value, message }]
          return
        }
        const aggregation = this.fieldType(value) === 'number' ? 'sum' : 'count'
        const exists = this.valueFields.some(item => item.field === value && item.aggregation === aggregation)
        if (!exists) this.valueFields.push({ field: value, aggregation, label: '' })
        this.selectionProblemsList = this.currentProblems()
      },
      removeField (list, index) {
        list.splice(index, 1)
        this.refreshProblems()
      },
      moveField (list, index, direction) {
        moveListItem(list, index, direction)
      },
      async getSpreadSheet () {
        const spreadsheet = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}`, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })
        this.spreadsheet = spreadsheet.data
        const columns = (this.spreadsheet && this.spreadsheet.columns) || []
        this.fieldOptions = [
          { value: 'id', text: 'ID (system)', type: 'number' },
          { value: 'createdAt', text: 'Created At (system)', type: 'dateTime' },
          { value: 'updatedAt', text: 'Updated At (system)', type: 'dateTime' },
          ...columns
            .filter(column => column && column.uniqueTitle && !SKIPPED_TYPES.has(column.type))
            .map(column => ({
              value: column.uniqueTitle,
              text: column.title || column.uniqueTitle,
              type: column.type
            })),
          ...relatedViewColumnOptions(this.spreadsheet, this.allSpreadsheets).map(option => ({
            value: option.value,
            text: option.text,
            type: option.relatedFieldType
          }))
        ]
      },
      async showEditPivotComponent () {
        this.rowFields = Array.isArray(this.rowFields) ? this.rowFields.slice() : []
        this.columnFields = Array.isArray(this.columnFields) ? this.columnFields.slice() : []
        this.valueFields = Array.isArray(this.valueFields) ? this.valueFields.map(value => ({ ...value })) : []
        this.rowChoice = ''
        this.columnChoice = ''
        this.valueChoice = ''
        this.selectionProblemsList = []
        this.activeTab = 0
        try {
          await this.getSpreadSheet()
        } catch (err) {
          this.spreadsheet = null
          this.fieldOptions = []
        }
        this.refreshProblems()
        this.pivotEditVisible = true
      },
      save (event) {
        if (!this.parentDesigner) {
          if (event && event.preventDefault) event.preventDefault()
          this.selectionProblemsList = [{ area: 'Pivot', field: '', message: 'This pivot could not be saved. Close the dialog and open it again.' }]
          this.activeTab = 1
          return
        }
        const problems = this.currentProblems()
        if (problems.length) {
          if (event && event.preventDefault) event.preventDefault()
          this.selectionProblemsList = problems
          this.activeTab = 1
          return
        }
        this.selectionProblemsList = []
        const config = normalizePivotConfig({
          rowFields: this.rowFields,
          columnFields: this.columnFields,
          valueFields: this.valueFields,
          showSubtotals: this.showSubtotals,
          showRowGrandTotal: this.showRowGrandTotal,
          showColumnGrandTotal: this.showColumnGrandTotal
        })
        this.parentDesigner.saveActiveComponent({
          uniqueTitle: this.uniqueTitle,
          title: this.title,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          components: [],
          rowFields: config.rowFields,
          columnFields: config.columnFields,
          valueFields: config.valueFields,
          showSubtotals: config.showSubtotals,
          showRowGrandTotal: config.showRowGrandTotal,
          showColumnGrandTotal: config.showColumnGrandTotal,
          userFiltering: this.userFiltering,
          searchEnabled: !!this.searchEnabled,
          searchScope: this.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view',
          restrictUserFiltering: this.restrictUserFiltering,
          defaultUserFilters: this.defaultUserFilters,
          defaultUserFilterMatchMode: this.defaultUserFilterMatchMode,
          preFilters: this.preFilters,
          preFilterMatchMode: this.preFilterMatchMode,
          filterButtons: this.filterButtons,
          allowMultipleFilterButtons: !!this.allowMultipleFilterButtons,
          realtime: this.realtime,
          filterByPageRecord: !!this.filterByPageRecord
        })
      }
    }
  }
</script>

<style>
.pivot-wells {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.pivot-well {
  border: 1px solid #2a2a28;
  border-radius: 6px;
  padding: 12px;
  min-width: 0;
}
.pivot-well h3 {
  margin: 0 0 8px;
  color: #9ad12b;
  font-size: 0.78em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.pivot-field-list {
  list-style: none;
  margin: 0 0 10px;
  padding: 0;
}
.pivot-field-list li {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px 0;
  border-bottom: 1px solid #2a2a28;
  color: #e8e8e6;
  font-size: 0.9em;
}
.pivot-field-actions {
  display: flex;
  gap: 6px;
}
.pivot-field-actions button,
.pivot-add .add-btn {
  background: transparent;
  color: #b0b0ae;
  border: 1px solid #3a3a38;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 0.78em;
}
.pivot-field-actions button:disabled {
  opacity: 0.4;
}
.pivot-add {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pivot-value-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #7a7a78;
  font-size: 0.78em;
}
.pivot-totals .checkbox-label {
  display: block;
  margin-bottom: 8px;
}
.pivot-save-error,
.pivot-field-error {
  color: #ffb4b4;
  margin: 0 0 12px;
}
.pivot-save-error ul {
  margin: 6px 0 0;
  padding-left: 18px;
}
.pivot-field-error {
  margin: 0;
  font-size: 0.85em;
}
@media (max-width: 900px) {
  .pivot-wells {
    grid-template-columns: 1fr;
  }
}
</style>
